/**
 * Money primitives — integer cents, basis points, and scaled rates.
 * Formulas and rounding: docs/03 § integer arithmetic; ADR-003.
 */

export type Cents = bigint & { readonly __brand: 'Cents' };
export type Bps = number & { readonly __brand: 'Bps' };
export type MilliPointsPerDollar = number & { readonly __brand: 'MilliPointsPerDollar' };
export type MilliCentsPerPoint = number & { readonly __brand: 'MilliCentsPerPoint' };
export type FxRateMicros = bigint & { readonly __brand: 'FxRateMicros' };

function assertInteger(n: number, label: string): void {
  if (!Number.isInteger(n)) {
    throw new Error(`${label} must be an integer`);
  }
}

export function asCents(n: bigint | number): Cents {
  if (typeof n === 'number') {
    assertInteger(n, 'Cents');
    return BigInt(n) as Cents;
  }
  return n as Cents;
}

export function asBps(n: number): Bps {
  assertInteger(n, 'Bps');
  return n as Bps;
}

export function asMilliPointsPerDollar(n: number): MilliPointsPerDollar {
  assertInteger(n, 'milli_points_per_dollar');
  return n as MilliPointsPerDollar;
}

export function asMilliCentsPerPoint(n: number): MilliCentsPerPoint {
  assertInteger(n, 'milli_cents_per_point');
  return n as MilliCentsPerPoint;
}

export function asFxRateMicros(n: bigint | number): FxRateMicros {
  if (typeof n === 'number') {
    assertInteger(n, 'fx_rate_micros');
    return BigInt(n) as FxRateMicros;
  }
  return n as FxRateMicros;
}

/** Round |n|/d half away from zero (docs/03: rewards round half-up). */
export function roundHalfUp(n: bigint, d: bigint): bigint {
  if (d <= 0n) throw new Error('denominator must be positive');
  const neg = n < 0n;
  const an = neg ? -n : n;
  const q = an / d;
  const r = an % d;
  const rounded = r * 2n >= d ? q + 1n : q;
  return neg ? -rounded : rounded;
}

/** Ceiling of |n|/d away from zero (docs/03: interest rounds up). */
export function roundUp(n: bigint, d: bigint): bigint {
  if (d <= 0n) throw new Error('denominator must be positive');
  const neg = n < 0n;
  const an = neg ? -n : n;
  const q = an / d;
  const r = an % d;
  const rounded = r === 0n ? q : q + 1n;
  return neg ? -rounded : rounded;
}

/** cashback_cents = round_half_up(amount_cents * reward_rate_bps / 10_000) */
export function cashbackCents(amount: Cents, rewardRateBps: Bps): Cents {
  return asCents(roundHalfUp(amount * BigInt(rewardRateBps), 10_000n));
}

/**
 * points_reward_cents = round_half_up(
 *   amount_cents * milli_points_per_dollar * milli_cents_per_point / 100_000_000
 * )
 * Null milli_cents_per_point → 0 (docs/03: never invent a cent-per-point value).
 */
export function pointsRewardCents(
  amount: Cents,
  milliPointsPerDollar: MilliPointsPerDollar,
  milliCentsPerPoint: MilliCentsPerPoint | null,
): Cents {
  if (milliCentsPerPoint === null) return asCents(0n);
  return asCents(
    roundHalfUp(amount * BigInt(milliPointsPerDollar) * BigInt(milliCentsPerPoint), 100_000_000n),
  );
}

/** fx_fee_cents = round_half_up(amount_cents * foreign_txn_fee_bps / 10_000) */
export function fxFeeCents(amount: Cents, foreignTxnFeeBps: Bps): Cents {
  return asCents(roundHalfUp(amount * BigInt(foreignTxnFeeBps), 10_000n));
}

/** monthly_interest_cents = round_up(amount_cents * purchase_apr_bps / 120_000) */
export function monthlyInterestCents(amount: Cents, purchaseAprBps: Bps): Cents {
  return asCents(roundUp(amount * BigInt(purchaseAprBps), 120_000n));
}

/**
 * Convert an amount using fx_rate_micros (×1_000_000 scale per docs/01).
 * home_cents = round_half_up(original_cents * fx_rate_micros / 1_000_000)
 */
export function convertWithFxRate(originalAmount: Cents, fxRateMicros: FxRateMicros): Cents {
  return asCents(roundHalfUp(originalAmount * fxRateMicros, 1_000_000n));
}
